import { GraphModel } from '../graph/graph.model';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { ToolModel } from './tool.model';
import { Routes } from '../routes';
import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

type Params = {
  toolId: string;
  toolConnectionId: string;
  name: string;
  displayName: string;
  isExisting?: boolean;
  onSelected?: () => void;
};

export class SelectToolAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Select Tool';
  description = 'Select a tool to configure';
  inputSchema = z.object({
    toolId: z.string(),
    toolConnectionId: z.string(),
    name: z.string(),
    displayName: z.string(),
    isExisting: z.boolean().optional(),
    onSelected: z.function().optional() as any,
  });
  group = 'Tool';
  icon = 'check';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: {
        route: string;
        title?: string;
        displayTitle?: string;
        icon?: string;
      }) => void;
    },
    private readonly queryClient: QueryClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) && context.hasTargetedModelOfType(ToolModel)
    );
  }

  async execute(
    input: z.infer<typeof this.inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const tool = context.targetedModelOfType(ToolModel);

    Assert.assert(tool !== null, 'Tool not found');

    // If this is an existing tool, navigate to it
    if (input.isExisting) {
      this.navigator.pushAndReplace({
        route: Routes.tool.forId(input.name).toString(),
        title: input.displayName || input.name,
        displayTitle: input.displayName || input.name,
        icon: 'wrench-screwdriver',
      });
      return ok({});
    }

    // Otherwise, update the tool model with the metadata for a new tool
    tool.formState.set({
      name: input.displayName || input.name,
    });

    tool.toolMetadata.set({
      toolId: input.toolId,
      toolConnectionId: input.toolConnectionId,
    });

    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.tools.all({ orgId: this.orgId }),
    });
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.connections.all({ orgId: this.orgId }),
    });
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.tools.all({ orgId: this.orgId }),
    });
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.connections.all({ orgId: this.orgId }),
    });

    // Call the callback to signal selection is complete
    if (input.onSelected) {
      input.onSelected();
    }

    return ok({});
  }
}
